import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { RoleKey } from '@modules/base/desktop/api/types';

/**
 * Client-side preferences plus prototype-only controls (role preview, service-down simulation).
 * Real preferences (theme, density, sidebar) persist locally; role/service simulation lives in
 * sessionStorage and disappears once the mock API is replaced by the real backend.
 */
export interface ProtoState {
  role: RoleKey;
  /** 'org' uses the organization's brand colours; 'default' shows the platform default theme. */
  brandMode: 'org' | 'default';
  theme: 'system' | 'light' | 'dark';
  density: 'comfortable' | 'compact';
  sidebarCollapsed: boolean;
  serviceDown: boolean;
  emptyData: boolean;
  /** The prototype starts at the sign-in page instead of signed in as the previewed role. */
  requireSignIn: boolean;
  set: (patch: Partial<Omit<ProtoState, 'set'>>) => void;
}

/** Storage that never throws (private windows, blocked site data, previews). */
const safeSession: Storage = {
  get length() {
    return 0;
  },
  clear: () => {},
  key: () => null,
  getItem: (k) => {
    try {
      return window.sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k, v) => {
    try {
      window.sessionStorage.setItem(k, v);
    } catch {
      /* storage unavailable: preferences simply do not persist */
    }
  },
  removeItem: (k) => {
    try {
      window.sessionStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

export const useProto = create<ProtoState>()(
  persist(
    (set) => ({
      role: 'physio',
      brandMode: 'org',
      theme: 'light',
      density: 'comfortable',
      sidebarCollapsed: false,
      serviceDown: false,
      emptyData: false,
      requireSignIn: false,
      set: (patch) => set(patch),
    }),
    { name: 'athlete.prefs', storage: createJSONStorage(() => safeSession) },
  ),
);

/** Dev/test hooks: ?as=coach&brand=default&theme=dark&density=compact&service=down&auth=required */
export function applyUrlParams(search: string = window.location.search) {
  const p = new URLSearchParams(search);
  const patch: Partial<ProtoState> = {};
  const as = p.get('as');
  if (as === 'org_admin' || as === 'physio' || as === 'doctor' || as === 'coach') patch.role = as;
  const brand = p.get('brand');
  if (brand === 'default' || brand === 'org') patch.brandMode = brand;
  const theme = p.get('theme');
  if (theme === 'light' || theme === 'dark' || theme === 'system') patch.theme = theme;
  const density = p.get('density');
  if (density === 'compact' || density === 'comfortable') patch.density = density;
  if (p.get('service') === 'down') patch.serviceDown = true;
  if (p.get('service') === 'up') patch.serviceDown = false;
  if (p.get('auth') === 'required') patch.requireSignIn = true;
  if (p.get('auth') === 'off') patch.requireSignIn = false;
  if (p.get('empty') === '1') patch.emptyData = true;
  if (p.get('empty') === '0') patch.emptyData = false;
  if (p.get('collapsed') === '1') patch.sidebarCollapsed = true;
  if (Object.keys(patch).length) useProto.getState().set(patch);
}
